import React from 'react';

/**
 * Renders an overlay-mode letterhead: the uploaded image is the full page background
 * and each placed field is painted at its (x, y, width, height) percentage with the
 * value pulled from the estimate/invoice document.
 *
 * Used by both EstimateView and InvoiceView when letterhead.template_mode === 'overlay'.
 */

const usd = (n) => `$${(Number(n) || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }); }
  catch { return iso; }
};
const fmtDateOnly = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso + 'T00:00:00').toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }); }
  catch { return iso; }
};

const resolveFieldValue = (key, doc, field) => {
  switch (key) {
    case 'text':             return field?.default_text || '';
    case 'customer_name':    return doc.customer_name || '';
    case 'customer_email':   return doc.customer_email || '';
    case 'customer_phone':   return doc.customer_phone || '';
    case 'customer_address': return doc.customer_address || '';
    case 'document_number':  return doc.estimate_number || doc.invoice_number || '';
    case 'document_date':    return fmtDate(doc.created_at);
    case 'due_date':         return doc.due_date ? fmtDateOnly(doc.due_date) : '';
    case 'valid_until':      return doc.valid_until ? fmtDateOnly(doc.valid_until) : '';
    case 'subtotal':         return usd(doc.subtotal);
    case 'tax':              return usd(doc.tax_amount);
    case 'total':            return usd(doc.total);
    case 'paid':             return usd(doc.paid_amount);
    case 'balance_due':      return usd(doc.balance_due);
    case 'notes':            return doc.notes || '';
    default: return '';
  }
};

const LineItemsTable = ({ items, fontSize }) => (
  <table className="w-full" style={{ fontSize: `${Math.max(fontSize - 2, 9)}px`, borderCollapse: 'collapse' }}>
    <thead>
      <tr style={{ borderBottom: '1px solid currentColor' }}>
        <th style={{ textAlign: 'left',  padding: '2px 4px' }}>Description</th>
        <th style={{ textAlign: 'right', padding: '2px 4px', width: '12%' }}>Qty</th>
        <th style={{ textAlign: 'right', padding: '2px 4px', width: '20%' }}>Unit</th>
        <th style={{ textAlign: 'right', padding: '2px 4px', width: '22%' }}>Total</th>
      </tr>
    </thead>
    <tbody>
      {(items || []).map((it, i) => (
        <tr key={`${it.label}-${i}`} style={{ borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
          <td style={{ padding: '3px 4px' }}>{it.label}</td>
          <td style={{ padding: '3px 4px', textAlign: 'right' }}>{it.qty}</td>
          <td style={{ padding: '3px 4px', textAlign: 'right' }}>{usd(it.unit_price)}</td>
          <td style={{ padding: '3px 4px', textAlign: 'right', fontWeight: 600 }}>{usd(it.line_total)}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const OverlayLetterheadRenderer = ({ letterhead, doc, testid }) => {
  if (!letterhead?.image_base64) return null;
  const fields = letterhead.fields || [];
  return (
    <div
      data-testid={testid || 'overlay-letterhead-render'}
      className="relative w-full bg-white shadow-xl rounded-md border border-slate-200 mx-auto"
      style={{ maxWidth: '850px', aspectRatio: '8.5 / 11' }}
    >
      <img
        src={`data:image/png;base64,${letterhead.image_base64}`}
        alt={letterhead.name || 'Template'}
        className="absolute inset-0 w-full h-full object-contain pointer-events-none"
      />
      {fields.map((f, idx) => {
        const isTable = f.key === 'line_items';
        const isText = f.key === 'text';
        const value = resolveFieldValue(f.key, doc, f);
        return (
          <div
            key={`${f.key}-${idx}`}
            data-testid={`overlay-rendered-${f.key}`}
            style={{
              position: 'absolute',
              left: `${f.x}%`,
              top: `${f.y}%`,
              width: `${f.width}%`,
              height: `${f.height}%`,
              fontSize: `${f.font_size}px`,
              color: f.font_color || '#0f172a',
              fontWeight: f.bold ? 700 : 400,
              textAlign: f.align || 'left',
              overflow: 'hidden',
              padding: '2px 4px',
              lineHeight: 1.25,
              whiteSpace: isTable || f.key === 'notes' || isText ? 'pre-wrap' : 'normal',
            }}
          >
            {isTable
              ? <LineItemsTable items={doc.items_computed || doc.line_items} fontSize={f.font_size} />
              : value}
          </div>
        );
      })}
    </div>
  );
};

export default OverlayLetterheadRenderer;
